import { Box, Grid, IconButton, Tooltip, useTheme } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import { buildNewConnectedLegs } from "../../helpers/components/inputs";

const InsertIcon = ({ insertDestination }) => {
  return (
    <Tooltip title="Add Layover">
      <IconButton
        style={{
          position: "absolute",
          left: "-16px",
          bottom: "-24px",
          zIndex: 10
        }}
        onClick={() => insertDestination()}
        size="small"
      >
        <AddCircleIcon />
      </IconButton>
    </Tooltip>
  );
};

const DestinationRow = ({
  idx,
  destination,
  destinationsCount,
  DestinationRowComponent,
  editDestination,
  insertDestination,
  removeDestination,
  disabled
}) => {
  if (idx === 0 || idx === destinationsCount - 1) {
    return (
      <Grid
        item
        style={{ position: "relative" }}
        key={`destination-row-${idx}`}
      >
        <DestinationRowComponent
          idx={idx}
          destination={destination}
          editDestination={editDestination}
          label={idx === 0 ? "Start Point" : "End Point"}
          disabled={disabled}
        />
        {idx === 0 && !disabled && (
          <InsertIcon insertDestination={insertDestination} />
        )}
      </Grid>
    );
  }

  return (
    <Grid
      item
      container
      style={{ position: "relative" }}
      key={`destination-row-${idx}`}
      alignItems="center"
    >
      <Grid item sm={10}>
        <DestinationRowComponent
          label={`Layover ${idx}`}
          destination={destination || ""}
          editDestination={editDestination}
          disabled={disabled}
        />
      </Grid>
      {!disabled && (
        <Grid item sm={2}>
          <IconButton onClick={() => removeDestination()} size="large">
            <CancelIcon />
          </IconButton>
        </Grid>
      )}
      {!disabled && <InsertIcon insertDestination={insertDestination} />}
    </Grid>
  );
};

const Routes = ({
  route,
  editRoute,
  onEditDestination,
  DestinationRowComponent,
  LegColumnComponent,
  legColumnComponentProps,
  defaultLegProp,
  findNewLegs,
  buildLeg,
  onEditLeg,
  disabled
}) => {
  const theme = useTheme();

  const { destinations = [], legs = [] } = route || {};

  const editDestination = (idx) => async (value) => {
    const newDestinations = destinations.map((destination, dIdx) =>
      idx === dIdx ? value : destination
    );

    const newLegs = await buildNewConnectedLegs({
      destinationIdx: idx,
      destinations: newDestinations,
      legs,
      findNewLegs,
      buildLeg
    });

    if (onEditDestination) {
      onEditDestination(value, idx);
    }

    return editRoute({ destinations: newDestinations, legs: newLegs });
  };

  const editLeg = (idx) => async (value) => {
    const newLegs = await onEditLeg(value, idx);

    return editRoute({ legs: newLegs });
  };

  const insertDestination = (destinationIdx) => async () => {
    const arrayInsert = (array, index, element) => [
      ...array.slice(0, index),
      element,
      ...array.slice(index)
    ];

    const newLegs = arrayInsert(legs, destinationIdx + 1, defaultLegProp);

    const newDestinations = arrayInsert(destinations, destinationIdx + 1, {});

    const newConnectedLegs = await buildNewConnectedLegs({
      destinationIdx: destinationIdx + 1,
      destinations: newDestinations,
      legs: newLegs,
      findNewLegs,
      buildLeg
    });
    return editRoute({ destinations: newDestinations, legs: newConnectedLegs });
  };

  const removeDestination = (destinationIdx) => async () => {
    const newDestinations = destinations.filter(
      (_, idx) => idx !== destinationIdx
    );
    const newLegs = legs.filter((_, idx) => idx !== destinationIdx);

    const newConnectedLegs = await buildNewConnectedLegs({
      destinationIdx,
      destinations: newDestinations,
      legs: newLegs,
      findNewLegs,
      buildLeg
    });
    return editRoute({ destinations: newDestinations, legs: newConnectedLegs });
  };

  return (
    <Box
      style={{
        padding: theme.spacing(2),
        position: "relative",
        overflowX: "hidden",
        overflowY: "auto",
        width: "100%"
      }}
    >
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        flexWrap="nowrap"
      >
        <Grid
          item
          sm={11}
          container
          spacing={1}
          alignItems="stretch"
          justifyContent="center"
          flexWrap="nowrap"
        >
          <Grid item xs={7} container direction="column" spacing={2}>
            {destinations.map((destination, idx) => (
              <DestinationRow
                key={`destination-row-${idx}`}
                idx={idx}
                DestinationRowComponent={DestinationRowComponent}
                destination={destination}
                destinationsCount={destinations.length}
                editDestination={editDestination(idx)}
                insertDestination={insertDestination(idx)}
                removeDestination={removeDestination(idx)}
                disabled={disabled}
              />
            ))}
          </Grid>
          <Grid
            item
            xs={5}
            container
            direction="column"
            spacing={2}
            justifyContent="center"
          >
            {legs.map((leg, idx) => (
              <Grid item key={`leg-column-${idx}`}>
                <LegColumnComponent
                  leg={leg}
                  idx={idx}
                  {...legColumnComponentProps}
                  editLeg={editLeg(idx)}
                  disabled={disabled}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Routes;
